import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  FormHelperText,
  Select,
  Divider,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Center,
  Spinner,
  Checkbox,
  CheckboxGroup,
  Stack,
  Icon,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiTag } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc, 
  Timestamp, 
  collection, 
  getDocs, 
  deleteDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { getAllServiceTypes, ServiceType, getAllUsers, User } from '../../services/firestore';

export interface PromoCode {
  id?: string;
  code: string; // Uppercase, unique
  discountType: "percentage" | "fixed";
  discountValue: number; // 20 for 20%, or dollar amount for fixed
  isActive: boolean;
  expiryDate?: any; // Firestore Timestamp or ISO string
  usageLimit?: number; // 0 = unlimited
  usedCount?: number;
  minOrderAmount?: number; // In cents
  applicableServices?: "all" | string[]; // "all" or array of service type IDs
  allowedCustomers?: string[]; // Empty array = all customers
  createdAt?: any;
  createdBy?: string;
  description?: string;
  lastUsedAt?: any;
}

const DEFAULT_PROMO_CODE: Omit<PromoCode, 'id'> = {
  code: '',
  discountType: 'percentage',
  discountValue: 10,
  isActive: true,
  usageLimit: 0,
  usedCount: 0,
  minOrderAmount: 0,
  applicableServices: 'all',
  allowedCustomers: [],
  description: '',
};

const PromoCodeManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  const [promoCodeForm, setPromoCodeForm] = useState<Omit<PromoCode, 'id'>>(DEFAULT_PROMO_CODE);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [deletingCode, setDeletingCode] = useState<PromoCode | null>(null);

  useEffect(() => {
    loadPromoCodes();
    loadServiceTypes();
    loadUsers();
  }, []);

  const loadServiceTypes = async () => {
    try {
      const services = await getAllServiceTypes();
      setServiceTypes(services);
    } catch (error) {
      console.error('Error loading service types:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const allUsers = await getAllUsers();
      // Sort by name for easier selection
      const sortedUsers = allUsers.sort((a, b) => {
        const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        return aName.localeCompare(bName);
      });
      setUsers(sortedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const getUserDisplayName = (user: User): string => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const name = `${firstName} ${lastName}`.trim();
    if (name) {
      return `${name} (${user.email})`;
    }
    return user.email;
  };

  const loadPromoCodes = async () => {
    try {
      setLoading(true);
      const promoCodesRef = collection(db, 'promoCodes');
      const q = query(promoCodesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const loadedCodes: PromoCode[] = [];
      snapshot.forEach((doc) => {
        loadedCodes.push({ id: doc.id, ...doc.data() } as PromoCode);
      });
      
      setPromoCodes(loadedCodes);
      console.log(`✅ Loaded ${loadedCodes.length} promo codes`);
    } catch (error) {
      console.error('Error loading promo codes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load promo codes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPromoCode = () => {
    setEditingPromoCode(null);
    setPromoCodeForm(DEFAULT_PROMO_CODE);
    setSelectedServiceIds([]);
    setSelectedCustomerIds([]);
    onModalOpen();
  };

  const handleEditPromoCode = (promoCode: PromoCode) => {
    setEditingPromoCode(promoCode);
    setPromoCodeForm({
      code: promoCode.code,
      discountType: promoCode.discountType,
      discountValue: promoCode.discountValue,
      isActive: promoCode.isActive,
      expiryDate: promoCode.expiryDate,
      usageLimit: promoCode.usageLimit || 0,
      usedCount: promoCode.usedCount || 0,
      minOrderAmount: promoCode.minOrderAmount || 0,
      applicableServices: promoCode.applicableServices || 'all',
      allowedCustomers: promoCode.allowedCustomers || [],
      description: promoCode.description || '',
    });
    
    // Set selected service IDs if applicableServices is an array
    if (Array.isArray(promoCode.applicableServices)) {
      setSelectedServiceIds(promoCode.applicableServices);
    } else {
      setSelectedServiceIds([]);
    }
    
    // Set selected customer IDs
    if (promoCode.allowedCustomers && promoCode.allowedCustomers.length > 0) {
      setSelectedCustomerIds(promoCode.allowedCustomers);
    } else {
      setSelectedCustomerIds([]);
    }
    
    onModalOpen();
  };

  const handleDeletePromoCode = (promoCode: PromoCode) => {
    setDeletingCode(promoCode);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    if (!deletingCode || !deletingCode.id) return;

    try {
      setSaving(true);
      await deleteDoc(doc(db, 'promoCodes', deletingCode.id));
      
      toast({
        title: 'Success',
        description: `Promo code "${deletingCode.code}" deleted successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      await loadPromoCodes();
      onDeleteClose();
      setDeletingCode(null);
    } catch (error) {
      console.error('Error deleting promo code:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete promo code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePromoCode = async () => {
    // Validation
    if (!promoCodeForm.code || promoCodeForm.code.trim() === '') {
      toast({
        title: 'Validation Error',
        description: 'Promo code is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (promoCodeForm.discountValue <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Discount value must be greater than 0',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (promoCodeForm.discountType === 'percentage' && promoCodeForm.discountValue > 100) {
      toast({
        title: 'Validation Error',
        description: 'Percentage discount cannot exceed 100%',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSaving(true);
      const codeUpper = promoCodeForm.code.toUpperCase().trim();
      
      // Prepare applicable services
      let applicableServices: "all" | string[] = 'all';
      if (selectedServiceIds.length > 0) {
        applicableServices = selectedServiceIds;
      }

      // Prepare expiry date
      let expiryDate: any = null;
      if (promoCodeForm.expiryDate) {
        if (promoCodeForm.expiryDate instanceof Date) {
          expiryDate = Timestamp.fromDate(promoCodeForm.expiryDate);
        } else if (promoCodeForm.expiryDate.toDate) {
          expiryDate = promoCodeForm.expiryDate;
        } else {
          expiryDate = Timestamp.fromDate(new Date(promoCodeForm.expiryDate));
        }
      }

      // Build promo code data, excluding undefined values
      const promoCodeData: any = {
        code: codeUpper,
        discountType: promoCodeForm.discountType,
        discountValue: promoCodeForm.discountValue,
        isActive: promoCodeForm.isActive,
        usageLimit: promoCodeForm.usageLimit || 0,
        usedCount: editingPromoCode?.usedCount || 0,
        minOrderAmount: promoCodeForm.minOrderAmount || 0,
        applicableServices,
        allowedCustomers: selectedCustomerIds || [],
        description: promoCodeForm.description || '',
        createdBy: currentUser?.uid || '',
      };

      // Only include expiryDate if it exists
      if (expiryDate) {
        promoCodeData.expiryDate = expiryDate;
      }

      // Only include lastUsedAt if it exists (for updates)
      if (editingPromoCode?.lastUsedAt) {
        promoCodeData.lastUsedAt = editingPromoCode.lastUsedAt;
      }

      if (editingPromoCode?.id) {
        // Update existing
        const promoCodeRef = doc(db, 'promoCodes', editingPromoCode.id);
        await updateDoc(promoCodeRef, {
          ...promoCodeData,
          updatedAt: Timestamp.now(),
        });
        
        toast({
          title: 'Success',
          description: `Promo code "${codeUpper}" updated successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Check if code already exists
        const existingQuery = query(
          collection(db, 'promoCodes'),
          where('code', '==', codeUpper)
        );
        const existingSnapshot = await getDocs(existingQuery);
        
        if (!existingSnapshot.empty) {
          toast({
            title: 'Error',
            description: `Promo code "${codeUpper}" already exists`,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        // Create new
        const promoCodeRef = doc(db, 'promoCodes', codeUpper);
        await setDoc(promoCodeRef, {
          ...promoCodeData,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        
        toast({
          title: 'Success',
          description: `Promo code "${codeUpper}" created successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      await loadPromoCodes();
      onModalClose();
      setEditingPromoCode(null);
      setPromoCodeForm(DEFAULT_PROMO_CODE);
      setSelectedServiceIds([]);
      setSelectedCustomerIds([]);
    } catch (error) {
      console.error('Error saving promo code:', error);
      toast({
        title: 'Error',
        description: 'Failed to save promo code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date: any): string => {
    if (!date) return 'N/A';
    try {
      if (date.toDate) {
        return date.toDate().toLocaleDateString();
      } else if (date.seconds) {
        return new Date(date.seconds * 1000).toLocaleDateString();
      } else {
        return new Date(date).toLocaleDateString();
      }
    } catch {
      return 'Invalid date';
    }
  };

  const formatCurrency = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getDiscountDisplay = (promoCode: PromoCode): string => {
    if (promoCode.discountType === 'percentage') {
      return `${promoCode.discountValue}%`;
    } else {
      return formatCurrency(promoCode.discountValue * 100); // Convert to cents
    }
  };

  const isExpired = (promoCode: PromoCode): boolean => {
    if (!promoCode.expiryDate) return false;
    try {
      let expiryDate: Date;
      if (promoCode.expiryDate.toDate) {
        expiryDate = promoCode.expiryDate.toDate();
      } else if (promoCode.expiryDate.seconds) {
        expiryDate = new Date(promoCode.expiryDate.seconds * 1000);
      } else {
        expiryDate = new Date(promoCode.expiryDate);
      }
      return new Date() > expiryDate;
    } catch {
      return false;
    }
  };

  const isUsageLimitReached = (promoCode: PromoCode): boolean => {
    if (!promoCode.usageLimit || promoCode.usageLimit === 0) return false;
    const usedCount = promoCode.usedCount || 0;
    return usedCount >= promoCode.usageLimit;
  };

  if (loading) {
    return (
      <Center py={10}>
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box>
      <Heading 
        size={{ base: 'md', md: 'lg' }}
        mb={2}
        pl={{ base: '60px', lg: 0 }}
        pr={{ base: 4, lg: 0 }}
      >
        Promo Code Management
      </Heading>
      <Text color="gray.600" mb={6}>
        Create and manage promotional discount codes for your customers
      </Text>

      <HStack mb={6} spacing={4}>
        <Button
          leftIcon={React.createElement(FiPlus as React.ElementType)}
          colorScheme="blue"
          onClick={handleAddPromoCode}
        >
          Add Promo Code
        </Button>
      </HStack>

      {promoCodes.length === 0 ? (
        <Card>
          <CardBody>
            <Center py={10}>
              <VStack spacing={4}>
                <Icon as={FiTag as React.ElementType} boxSize={12} color="gray.400" />
                <Text color="gray.500">No promo codes found</Text>
                <Button
                  leftIcon={React.createElement(FiPlus as React.ElementType)}
                  colorScheme="blue"
                  onClick={handleAddPromoCode}
                >
                  Create Your First Promo Code
                </Button>
              </VStack>
            </Center>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Code</Th>
                  <Th>Discount</Th>
                  <Th>Status</Th>
                  <Th>Usage</Th>
                  <Th>Expiry</Th>
                  <Th>Min Order</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {promoCodes.map((promoCode) => {
                  const expired = isExpired(promoCode);
                  const limitReached = isUsageLimitReached(promoCode);
                  
                  return (
                    <Tr key={promoCode.id}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">{promoCode.code}</Text>
                          {promoCode.description && (
                            <Text fontSize="xs" color="gray.500">
                              {promoCode.description}
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Badge colorScheme="green" fontSize="sm">
                          {getDiscountDisplay(promoCode)}
                        </Badge>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Badge
                            colorScheme={promoCode.isActive ? 'green' : 'gray'}
                          >
                            {promoCode.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          {expired && (
                            <Badge colorScheme="red" fontSize="xs">
                              Expired
                            </Badge>
                          )}
                          {limitReached && (
                            <Badge colorScheme="orange" fontSize="xs">
                              Limit Reached
                            </Badge>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {promoCode.usedCount || 0}
                          {promoCode.usageLimit && promoCode.usageLimit > 0
                            ? ` / ${promoCode.usageLimit}`
                            : ' / ∞'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">{formatDate(promoCode.expiryDate)}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {promoCode.minOrderAmount && promoCode.minOrderAmount > 0
                            ? formatCurrency(promoCode.minOrderAmount)
                            : 'None'}
                        </Text>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Edit">
                            <IconButton
                              aria-label="Edit promo code"
                              icon={React.createElement(FiEdit2 as React.ElementType)}
                              size="sm"
                              onClick={() => handleEditPromoCode(promoCode)}
                            />
                          </Tooltip>
                          <Tooltip label="Delete">
                            <IconButton
                              aria-label="Delete promo code"
                              icon={React.createElement(FiTrash2 as React.ElementType)}
                              size="sm"
                              colorScheme="red"
                              onClick={() => handleDeletePromoCode(promoCode)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingPromoCode ? 'Edit Promo Code' : 'Create Promo Code'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Promo Code</FormLabel>
                <Input
                  value={promoCodeForm.code}
                  onChange={(e) =>
                    setPromoCodeForm({
                      ...promoCodeForm,
                      code: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="SAVE20"
                  isDisabled={!!editingPromoCode}
                />
                <FormHelperText>
                  Code will be automatically converted to uppercase
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={promoCodeForm.description || ''}
                  onChange={(e) =>
                    setPromoCodeForm({
                      ...promoCodeForm,
                      description: e.target.value,
                    })
                  }
                  placeholder="Optional description for internal use"
                />
              </FormControl>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Discount Type</FormLabel>
                  <Select
                    value={promoCodeForm.discountType}
                    onChange={(e) =>
                      setPromoCodeForm({
                        ...promoCodeForm,
                        discountType: e.target.value as 'percentage' | 'fixed',
                      })
                    }
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>
                    {promoCodeForm.discountType === 'percentage'
                      ? 'Discount Percentage'
                      : 'Discount Amount ($)'}
                  </FormLabel>
                  <NumberInput
                    value={promoCodeForm.discountValue}
                    onChange={(_, value) =>
                      setPromoCodeForm({
                        ...promoCodeForm,
                        discountValue: isNaN(value) ? 0 : value,
                      })
                    }
                    min={0}
                    max={promoCodeForm.discountType === 'percentage' ? 100 : undefined}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText>
                    {promoCodeForm.discountType === 'percentage'
                      ? 'Enter percentage (e.g., 20 for 20%)'
                      : 'Enter dollar amount (e.g., 10 for $10)'}
                  </FormHelperText>
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl>
                  <FormLabel>Usage Limit</FormLabel>
                  <NumberInput
                    value={promoCodeForm.usageLimit || 0}
                    onChange={(_, value) =>
                      setPromoCodeForm({
                        ...promoCodeForm,
                        usageLimit: isNaN(value) ? 0 : value,
                      })
                    }
                    min={0}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText>0 = unlimited</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Minimum Order Amount ($)</FormLabel>
                  <NumberInput
                    value={(promoCodeForm.minOrderAmount || 0) / 100}
                    onChange={(_, value) =>
                      setPromoCodeForm({
                        ...promoCodeForm,
                        minOrderAmount: isNaN(value) ? 0 : Math.round(value * 100),
                      })
                    }
                    min={0}
                    precision={2}
                    step={0.01}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText>Minimum order required to use this code</FormHelperText>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Expiry Date</FormLabel>
                <Input
                  type="datetime-local"
                  value={
                    promoCodeForm.expiryDate
                      ? new Date(
                          promoCodeForm.expiryDate.toDate
                            ? promoCodeForm.expiryDate.toDate()
                            : promoCodeForm.expiryDate.seconds
                            ? new Date(promoCodeForm.expiryDate.seconds * 1000)
                            : new Date(promoCodeForm.expiryDate)
                        )
                          .toISOString()
                          .slice(0, 16)
                      : ''
                  }
                  onChange={(e) => {
                    if (e.target.value) {
                      setPromoCodeForm({
                        ...promoCodeForm,
                        expiryDate: Timestamp.fromDate(new Date(e.target.value)),
                      });
                    } else {
                      setPromoCodeForm({
                        ...promoCodeForm,
                        expiryDate: undefined,
                      });
                    }
                  }}
                />
                <FormHelperText>Leave empty for no expiry</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Applicable Services</FormLabel>
                <Select
                  value={
                    Array.isArray(promoCodeForm.applicableServices)
                      ? 'specific'
                      : promoCodeForm.applicableServices || 'all'
                  }
                  onChange={(e) => {
                    if (e.target.value === 'all') {
                      setPromoCodeForm({
                        ...promoCodeForm,
                        applicableServices: 'all',
                      });
                      setSelectedServiceIds([]);
                    } else {
                      setPromoCodeForm({
                        ...promoCodeForm,
                        applicableServices: selectedServiceIds,
                      });
                    }
                  }}
                >
                  <option value="all">All Services</option>
                  <option value="specific">Specific Services</option>
                </Select>
                {Array.isArray(promoCodeForm.applicableServices) ||
                (promoCodeForm.applicableServices !== 'all' &&
                  selectedServiceIds.length > 0) ? (
                  <Box mt={4} p={4} borderWidth={1} borderRadius="md">
                    <FormLabel fontSize="sm">Select Services</FormLabel>
                    <CheckboxGroup
                      value={selectedServiceIds}
                      onChange={(values) => {
                        const ids = values as string[];
                        setSelectedServiceIds(ids);
                        setPromoCodeForm({
                          ...promoCodeForm,
                          applicableServices: ids.length > 0 ? ids : 'all',
                        });
                      }}
                    >
                      <Stack spacing={2} maxH="200px" overflowY="auto">
                        {serviceTypes.map((service) => (
                          <Checkbox key={service.id} value={service.id || ''}>
                            {service.name}
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  </Box>
                ) : null}
              </FormControl>

              <FormControl>
                <FormLabel>Allowed Customers</FormLabel>
                <FormHelperText mb={2}>
                  Leave as "All Customers" to make this code available to everyone
                </FormHelperText>
                <Box mt={2} p={4} borderWidth={1} borderRadius="md" bg="gray.50">
                  <HStack justify="space-between" mb={3}>
                    <FormLabel fontSize="sm" mb={0}>Select Customers</FormLabel>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => {
                        if (selectedCustomerIds.length === users.length) {
                          setSelectedCustomerIds([]);
                          setPromoCodeForm({
                            ...promoCodeForm,
                            allowedCustomers: [],
                          });
                        } else {
                          const allIds = users.map(u => u.id);
                          setSelectedCustomerIds(allIds);
                          setPromoCodeForm({
                            ...promoCodeForm,
                            allowedCustomers: allIds,
                          });
                        }
                      }}
                    >
                      {selectedCustomerIds.length === users.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </HStack>
                  <CheckboxGroup
                    value={selectedCustomerIds}
                    onChange={(values) => {
                      const ids = values as string[];
                      setSelectedCustomerIds(ids);
                      setPromoCodeForm({
                        ...promoCodeForm,
                        allowedCustomers: ids,
                      });
                    }}
                  >
                    <Stack spacing={2} maxH="250px" overflowY="auto">
                      {users.length === 0 ? (
                        <Text fontSize="sm" color="gray.500">No customers found</Text>
                      ) : (
                        users.map((user) => (
                          <Checkbox key={user.id} value={user.id}>
                            {getUserDisplayName(user)}
                          </Checkbox>
                        ))
                      )}
                    </Stack>
                  </CheckboxGroup>
                  {selectedCustomerIds.length === 0 ? (
                    <Text fontSize="xs" color="green.600" mt={2} fontWeight="medium">
                      ✓ Available to all customers
                    </Text>
                  ) : (
                    <Text fontSize="xs" color="blue.600" mt={2} fontWeight="medium">
                      Restricted to {selectedCustomerIds.length} customer{selectedCustomerIds.length !== 1 ? 's' : ''}
                    </Text>
                  )}
                </Box>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Active</FormLabel>
                <Switch
                  isChecked={promoCodeForm.isActive}
                  onChange={(e) =>
                    setPromoCodeForm({
                      ...promoCodeForm,
                      isActive: e.target.checked,
                    })
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSavePromoCode}
              isLoading={saving}
            >
              {editingPromoCode ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Promo Code</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete promo code "{deletingCode?.code}"?
              This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={confirmDelete}
              isLoading={saving}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PromoCodeManagement;

